import { useState, useEffect } from "react";

/**
 * A custom React hook to allow functional components to run async methods.
 * This hook returns a loading state variable that will trigger a render of the using Component.
 * The result of the async function is returned in the result variable, but you can ignore this if the async function
 * is updating internal state of a different object ( see RegexDlfEditor reuse calculation for a reference)
 *
 * Unmounted components are handled within the `if(mounted)` branch.
 *
 * @param actionAsync
 * @param initialResult
 * @returns [result, trigger, loading, setResult]
 */

function useAsync<R = any>(
  actionAsync: (...args: any[]) => Promise<R>,
  initialResult: R,
): [R, (...args: any[]) => void, boolean, (overrideResult: R) => void] {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<R>(initialResult);
  const [fetchFlag, setFetchFlag] = useState(0);

  const [asyncArg, setArgs] = useState<any[]>();

  useEffect(() => {
    if (fetchFlag == 0) {
      // Run only after triggerFetch is called
      return;
    }
    let mounted = true;
    setLoading(true);
    // safe to assume populated, as triggerFetch() delcaration enforces correct typing... right ?
    actionAsync(...(asyncArg ?? [])).then((res) => {
      if (mounted) {
        // Only modify state if component is still mounted
        setLoading(false);
        setResult(res);
      }
    });
    // Signal that compnoent has been 'cleaned up'
    return () => {
      mounted = false;
    };
  }, [fetchFlag]);

  function triggerFetch(...args: []): void {
    setArgs(args);
    // Set fetchFlag to indirectly trigger the useEffect above
    setFetchFlag(Math.random() + 1); // + 1 to avoid generating 0
  }
  return [result, triggerFetch, loading, setResult];
}

export { useAsync as useAsync };
